import "bootstrap/dist/css/bootstrap.min.css";
import { useState, useEffect, useRef } from "react";
import {
  Container,
  Form,
  Label,
  Row,
  Input,
  Col,
  Button,
  FormGroup,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ButtonGroup,
} from "reactstrap";
import { v4 as uuidv4 } from "uuid";
import "./styles/index.scss";
import { FaCog, FaInfo, FaCopy, FaCheck } from "react-icons/fa";
import "reactjs-popup/dist/index.css";
import Popup from "reactjs-popup";

function SegmentMessageModal({ isOpen, toggle, segment, message }) {
  return (
    <Modal centered autoFocus isOpen={isOpen} toggle={toggle} parentSelector={() => document.querySelector("#root")}>
      <ModalHeader toggle={toggle}>{segment}</ModalHeader>
      <ModalBody>{message}</ModalBody>
    </Modal>
  );
}

function App() {
  const [segments, setSegments] = useState([]);
  const [isFetchingMessage, setIsFetchingMessage] = useState(false);
  const [customisedMessages, setCustomisedMessages] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [uuid, setUuid] = useState(null);
  const [isLiked, setIsLiked] = useState(null);
  const [copiedSegment, setCopiedSegment] = useState(null);
  const toggle = () => setModalOpen(!modalOpen);
  const complete = Object.keys(customisedMessages).length === segments.length;
  const messageInputRef = useRef(null);

  function handleMessageInput(e) {
    const el = e.target;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }

  useEffect(() => {
    async function fetchSegments() {
      const data = await fetch("/api/segments");
      const json = await data.json();
      setSegments(json);
    }
    fetchSegments();
  }, []);

  async function fetchCustomisedMessage(uuid, segment, message, prompt = null, temperature = 3) {
    const data = {
      uuid,
      prompt,
      segment,
      message,
      temperature: temperature / 10,
    };
    setIsFetchingMessage(true);
    const response = await fetch("/api/customiseMessage", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    const json = await response.json();
    setIsFetchingMessage(false);
    window.scrollTo(0, document.body.scrollHeight);
    return json;
  }

  async function onSubmit(e) {
    e.preventDefault();
    const form = new FormData(e.target);
    const formData = Object.fromEntries(form.entries());
    const newUuid = uuidv4();
    setUuid(newUuid);
    setIsLiked(null);
    setCustomisedMessages({});
    for (const segment of segments) {
      const customisedMessage = await fetchCustomisedMessage(
        newUuid,
        segment.name,
        formData.message,
        formData.prompt,
        formData.temperature
      );
      setCustomisedMessages((messages) => ({
        ...messages,
        [segment.name]: customisedMessage,
      }));
    }
  }

  async function onLike(e, liked) {
    e.preventDefault();
    const data = {
      uuid,
      liked,
    };
    setIsLiked(liked);
    setIsFetchingMessage(true);
    const response = await fetch("/api/like", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    const json = await response.json();
    setIsFetchingMessage(false);
    return json;
  }

  function onOpenModal(segment, message) {
    setModalTitle(segment);
    setModalMessage(message);
    setModalOpen(true);
  }

  async function onCopyMessage(segment, message) {
    try {
      await navigator.clipboard.writeText(message);
      setCopiedSegment(segment);
      setTimeout(() => {
        setCopiedSegment((current) => (current === segment ? null : current));
      }, 2000);
    } catch (err) {
      console.error("Failed to copy message", err);
    }
  }
  return (
    <Container fluid>
      <div>
        <Row>
          <Col>
            <Form onSubmit={onSubmit} className="form-style">
              <div className="message-creativity-row">
                <FormGroup className="message-creativity-field">
                  <Label for="message">
                    <h3>Message</h3>
                    <div className="container-divider" style={{margin: "20px 2px 20px"}}></div>
                    <span style={{ fontSize: "small" }}>(Write your marketing message here)</span>
                  </Label>
                  <Input
                    name="message"
                    type="textarea"
                    rows={1}
                    innerRef={messageInputRef}
                    onChange={handleMessageInput}
                  />
                </FormGroup>
                <FormGroup className="message-creativity-field">
                  <Label for="temperature">
                    <h3>Creativity</h3>
                    <div className="container-divider" style={{margin: "20px 2px 20px"}}></div>
                    <span style={{ fontSize: "small" }}>(Decide how creative the message can be)</span>
                  </Label>
                  <Input name="temperature" type="range" min={0} max={10} defaultValue={3} width="100%" />
                  <Row>
                    <Col>
                      <span style={{ fontSize: "small" }}>Predictable</span>
                    </Col>
                    <Col style={{ textAlign: "right" }}>
                      <span style={{ fontSize: "small" }}>Creative</span>
                    </Col>
                  </Row>
                </FormGroup>
              </div>
              <Button disabled={isFetchingMessage} color="custom-button align-items-center">
                <FaCog /> <span>GENERATE</span>
              </Button>{" "}
              {isFetchingMessage && <Spinner />}
              <br />
              <br />
              <p>Messages may take about 10 seconds to generate per segment</p>
              <p>
                <b>Warning: AI generated messages may contain inaccuracies, misinformation or false statements</b>
              </p>              
            </Form>
          </Col>
        </Row>
        <Row className="pt-4">
          {Object.entries(customisedMessages).map(([segment, message]) => (
            <Col lg="3" md="6" className="mb-4" key={segment}>
              <div className="segment-card">
                <div className="segment-card-header">
                  <div className="segment-card-title">
                    <h4>{segment}</h4>
                  </div>
                  <Popup
                    trigger={
                      <span title={message.segment_description} className="segment-card-info-btn">
                        <FaInfo />
                      </span>
                    }
                    position="left center"
                  >
                    <div>{message.segment_description}</div>
                  </Popup>
                </div>
                <div className="segment-card-divider"></div>
                <p className="segment-card-body">
                  {message.customised_message.substring(0, 220)}
                  {message.customised_message.length > 220 ? "..." : ""}
                </p>
                <div className="segment-card-footer">
                  <button
                    className="segment-card-link"
                    onClick={() => onOpenModal(segment, message.customised_message)}
                  >
                    Continue reading
                  </button>
                  <button
                    type="button"
                    className="segment-card-copy-btn"
                    onClick={() => onCopyMessage(segment, message.customised_message)}
                    title="Copy message to clipboard"
                  >
                    {copiedSegment === segment ? (
                      <>
                        <FaCheck /> Copied
                      </>
                    ) : (
                      <>
                        <FaCopy /> Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
            </Col>
          ))}
          {isFetchingMessage && (
            <Col>
              <Spinner />
            </Col>
          )}
        </Row>
      </div>
      <SegmentMessageModal isOpen={modalOpen} toggle={toggle} segment={modalTitle} message={modalMessage} />
    </Container>
  );
}

export default App;
